/**
 * Where the reseller is, as a download surface.
 *
 * Statsboard needs every download attributed to the place it was triggered from, and that is
 * only knowable in the browser at the moment of the click. This is the single place that
 * answers it, so a new download control gets attribution by calling one function rather than
 * by someone remembering to thread a string.
 *
 * ⚠️ Read from the GRID'S OWN STATE, not from the URL. `loaderState.filters` is what the
 * server was asked for, so it already distinguishes a collection from an artist page from a
 * search — including the cases a path cannot, like a search run from the front page. The
 * keys below are the same ones `search/load/filterBuilder.ts` reads.
 */
import { getLoader } from "../state/loaderState";
import type { DownloadSource } from "@brave-new-world/shared";

export interface DownloadSurface {
  source: DownloadSource;
  /**
   * Omitted rather than null when the surface points at nothing.
   *
   * This object is spread straight into the tRPC input, and `exactOptionalPropertyTypes`
   * makes "absent" and "present but null" different types at that boundary. Omitting is also
   * what the wire wants: a key that is not there is not sent.
   */
  sourceRef?: string;
}

/** A filter value as a non-empty string, or null. */
function str(value: string | number | undefined): string | null {
  if (value === undefined) return null;
  const s = String(value).trim();
  return s === "" || s === "0" ? null : s;
}

/**
 * The surface the current listing represents.
 *
 * ⚠️ Search outranks the container it was run in. Searching "forest" inside a collection is
 * attributed to the search, because the search is the act that surfaced the artwork — the
 * collection merely bounded it. Crediting the collection would overstate curation, which is
 * the specific thing the attribution exists to measure honestly.
 *
 * Everything that is not one of the four named containers is `browse`. That is a real
 * answer, not a fallback: the main grid is where most downloads come from.
 */
export function currentDownloadSurface(): DownloadSurface {
  const filters = getLoader()?.filters;
  if (!filters) {
    // No grid on this page — a detail view, or a page that renders its own controls. The
    // honest answer is that we do not know, and `unknown` is a bucket the reports show.
    return { source: "unknown" };
  }

  const q = str(filters.q);
  if (q !== null) return { source: "search", sourceRef: q };

  const list = str(filters.lightbox_id);
  if (list !== null) return { source: "list", sourceRef: list };

  const collection = str(filters.collection_id);
  if (collection !== null) return { source: "collection", sourceRef: collection };

  const artist = str(filters.artist_id);
  if (artist !== null) return { source: "artist_profile", sourceRef: artist };

  return { source: "browse" };
}

/**
 * The same, as query parameters for the plain-GET download route.
 *
 * `/ptdlfile` is a navigation, not a fetch, so its surface has to travel in the URL.
 */
export function downloadSurfaceQuery(): string {
  const { source, sourceRef } = currentDownloadSurface();
  const ref =
    sourceRef === undefined || sourceRef === "" ? "" : `&sref=${encodeURIComponent(sourceRef)}`;
  return `&src=${encodeURIComponent(source)}${ref}`;
}
